import React, { useCallback, useEffect, useState } from "react";
import {
  StickyNote,
  Loader2,
  Plus,
  Trash2,
  Send,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { useAuth } from "../lib/AuthProvider";
import {
  fetchSalesNotes,
  createSalesNote,
  deleteSalesNote,
} from "../lib/api";
import { toast } from "sonner";

function initials(name) {
  return (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");
}

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const now = new Date();
  const diff = Math.round((now - then) / 1000);
  if (diff < 60)        return "just now";
  if (diff < 3600)      return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400)     return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function exactTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Sales Notes popup — opens from the hero card button on Dispensary
 * Detail. Append-only log: new notes are saved as separate immutable
 * entries (with author + timestamp). Admins can delete an entry.
 *
 * Props:
 *  - dispensaryId, dispensaryName : context
 *  - trigger                       : the button that opens the dialog
 *                                    (rendered by the caller so the hero
 *                                    can show the live count)
 */
export function SalesNotesDialog({ dispensaryId, dispensaryName, trigger }) {
  const { isAdmin } = useAuth();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await fetchSalesNotes(dispensaryId);
      setNotes(list);
    } catch {
      setNotes([]);
    }
  }, [dispensaryId]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const submit = async (e) => {
    e?.preventDefault?.();
    const body = draft.trim();
    if (!body) return;
    try {
      setSaving(true);
      await createSalesNote(dispensaryId, body);
      setDraft("");
      toast.success("Note added");
      await load();
    } catch (err) {
      toast.error("Could not save note", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (note) => {
    if (
      !window.confirm(
        `Remove this note?\n\n"${(note.body || "").slice(0, 80)}…"\n\nThis can't be undone.`
      )
    )
      return;
    try {
      await deleteSalesNote(note.id);
      toast.success("Note removed");
      load();
    } catch (err) {
      toast.error("Could not remove", {
        description: err?.response?.data?.detail || err?.message,
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <span onClick={() => setOpen(true)}>{trigger}</span>
      <DialogContent
        data-testid="sales-notes-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl flex flex-col max-h-[85vh] p-0 gap-0"
      >
        <DialogHeader className="shrink-0 px-6 py-4 border-b border-zinc-800">
          <DialogTitle className="text-zinc-50 flex items-center gap-2">
            <StickyNote className="w-4 h-4 text-emerald-400" />
            Sales notes
            <span className="text-xs text-zinc-500 font-normal truncate ml-1">
              · {dispensaryName}
            </span>
          </DialogTitle>
          <p className="text-xs text-zinc-500 mt-1 leading-relaxed">
            A running log of context, updates, and reminders. New notes are
            added to the top — they never overwrite older notes.
          </p>
        </DialogHeader>

        {/* Add-note composer */}
        <form
          onSubmit={submit}
          className="shrink-0 px-6 py-4 border-b border-zinc-800 space-y-2"
          data-testid="sales-notes-composer"
        >
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="e.g. Maya promoted to lead buyer this week — she'll be our main contact. Wants to see Q3 deck."
            className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm resize-none focus-visible:ring-emerald-500/30"
            data-testid="sales-notes-input"
          />
          <div className="flex items-center justify-end">
            <Button
              type="submit"
              disabled={saving || !draft.trim()}
              data-testid="sales-notes-save-btn"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-8"
            >
              {saving ? (
                <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
              ) : (
                <Send className="w-3.5 h-3.5 mr-1.5" />
              )}
              Add note
            </Button>
          </div>
        </form>

        {/* History */}
        <div className="overflow-y-auto flex-1 min-h-0 px-6 py-4">
          {notes === null ? (
            <div className="text-sm text-zinc-500 flex items-center gap-2 py-4">
              <Loader2 className="w-4 h-4 animate-spin text-emerald-400" />
              Loading notes…
            </div>
          ) : notes.length === 0 ? (
            <div
              data-testid="sales-notes-empty"
              className="text-sm text-zinc-500 italic text-center py-8"
            >
              No notes yet — add the first one above.
            </div>
          ) : (
            <ol
              className="space-y-3"
              data-testid="sales-notes-list"
            >
              {notes.map((n) => (
                <li
                  key={n.id}
                  data-testid={`sales-note-${n.id}`}
                  className="group flex items-start gap-3 rounded-md border border-zinc-800 bg-zinc-900/40 px-3 py-2.5"
                >
                  <div className="shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-[10px] font-mono font-semibold text-emerald-200">
                    {initials(n.author_name || n.author_email || "?")}
                  </div>
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-semibold text-zinc-200">
                        {n.author_name ||
                          (n.author_email || "").split("@")[0] ||
                          "System"}
                      </span>
                      <span
                        className="text-zinc-500 font-mono"
                        title={exactTime(n.created_at)}
                      >
                        {relativeTime(n.created_at)}
                      </span>
                    </div>
                    <div className="text-sm text-zinc-100 leading-relaxed whitespace-pre-wrap break-words">
                      {n.body}
                    </div>
                  </div>
                  {isAdmin && (
                    <button
                      type="button"
                      data-testid={`sales-note-delete-${n.id}`}
                      onClick={() => remove(n)}
                      className="shrink-0 p-1.5 text-zinc-500 hover:text-rose-300 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      title="Remove (admin only)"
                      aria-label="Remove note"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Compact button used in the hero card. Wraps SalesNotesDialog and
 * shows a live note count so reps know if there's history before
 * opening the popup.
 */
export function SalesNotesHeroButton({ dispensaryId, dispensaryName }) {
  const [count, setCount] = useState(null);

  const load = useCallback(async () => {
    try {
      const list = await fetchSalesNotes(dispensaryId);
      setCount(list.length);
    } catch {
      setCount(0);
    }
  }, [dispensaryId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <SalesNotesDialog
      dispensaryId={dispensaryId}
      dispensaryName={dispensaryName}
      trigger={
        <button
          type="button"
          data-testid="hero-action-sales-notes"
          onClick={() => {
            // Refresh count when dialog closes (parent-level handler is
            // simpler than passing a callback through Dialog)
            setTimeout(load, 600);
          }}
          className="group flex items-center gap-3 px-4 py-3 rounded-lg border border-zinc-800 bg-zinc-900/40 hover:bg-zinc-900 hover:border-emerald-500/40 transition-colors min-w-[180px] text-left"
        >
          <div className="w-9 h-9 rounded-md bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center text-emerald-300 group-hover:bg-emerald-500/20">
            <StickyNote className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
              Sales notes
            </div>
            <div className="text-sm text-zinc-100 font-mono truncate">
              {count === null
                ? "…"
                : count === 0
                ? "Add the first"
                : `${count} note${count === 1 ? "" : "s"}`}
            </div>
          </div>
          {count > 0 && (
            <Plus className="w-3.5 h-3.5 text-zinc-500 ml-auto group-hover:text-emerald-400" />
          )}
        </button>
      }
    />
  );
}

export default SalesNotesDialog;
